import { useContext, useEffect, useState } from 'react';
import { useNavigate } from 'react-router';
import { AuthOnboardingLayout } from '../../components/login-v2/AuthShell';
import AuthBackHeader from '../../components/login-v2/AuthBackHeader';
import AuthMascot from '../../components/login-v2/AuthMascot';
import AuthPrimaryButton from '../../components/login-v2/AuthPrimaryButton';
import AuthTextField from '../../components/login-v2/AuthTextField';
import { AuthContext } from '../../context/AuthContext';
import { isSignupOnboarding, setSignupNickname } from './signupStorage';

const NICKNAME_REGEX = /^[A-Za-z0-9가-힣]{2,10}$/;

function hasStoredAuth() {
  return Boolean(localStorage.getItem('accessToken') && localStorage.getItem('refreshToken'));
}

function resolveToken(accessToken) {
  if (accessToken) return accessToken;
  try {
    return JSON.parse(localStorage.getItem('accessToken') || 'null')?.value || null;
  } catch {
    return null;
  }
}

export default function LoginV2NicknamePage() {
  const navigate = useNavigate();
  const { API_SERVER_DOMAIN, useAccessToken } = useContext(AuthContext);
  const accessToken = useAccessToken();

  const [nickname, setNickname] = useState('');
  const [errorText, setErrorText] = useState('');
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (!accessToken && !hasStoredAuth()) {
      navigate('/login', { replace: true });
      return;
    }
    if (accessToken && !isSignupOnboarding()) {
      navigate('/playground', { replace: true });
    }
  }, [accessToken, navigate]);

  const canSubmit = nickname.trim().length >= 2 && !submitting;

  const submitNickname = async () => {
    const value = nickname.trim();
    const token = resolveToken(accessToken);
    setErrorText('');

    if (!token) {
      navigate('/login', { replace: true });
      return;
    }

    if (!NICKNAME_REGEX.test(value)) {
      setErrorText('영문, 한글, 숫자 조합 2~10자로 입력해주세요.');
      return;
    }

    setSubmitting(true);
    try {
      const dupRes = await fetch(`${API_SERVER_DOMAIN}/user/userNo?nickname=${encodeURIComponent(value)}`);
      const dupData = await dupRes.json();
      const infos = Array.isArray(dupData?.infos) ? dupData.infos : [];
      if (infos.length > 0) {
        setErrorText('이미 사용중인 닉네임이예요. 다른 닉네임을 입력해주세요.');
        setSubmitting(false);
        return;
      }

      const response = await fetch(`${API_SERVER_DOMAIN}/user/profile/nickname`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ nickname: value }),
      });
      const jsonData = await response.json();
      if (jsonData.result === 'ok') {
        setSignupNickname(jsonData.info?.nickname || jsonData.info?.nickName || value);
        navigate('/login-v2/referral');
      } else {
        setErrorText(jsonData.message || '닉네임 저장에 실패했어요. 다시 시도해 주세요.');
      }
    } catch (error) {
      console.error(error);
      setErrorText('네트워크 오류가 발생했어요. 다시 시도해 주세요.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <AuthOnboardingLayout
      header={<AuthBackHeader onBack={() => navigate('/login-v2/terms')} />}
      actions={
        <AuthPrimaryButton disabled={!canSubmit} onClick={submitNickname}>
          확인
        </AuthPrimaryButton>
      }
    >
      <div className="mt-6 px-[10px]">
        <AuthMascot />
        <h1 className="mt-5 font-['Pretendard'] text-[22px] font-bold leading-[1.32] text-black">
          <span className="text-primary-purple-01">닉네임</span>을 입력해주세요
        </h1>
      </div>

      <div className="mt-10 px-[5px]">
        <AuthTextField
          value={nickname}
          onChange={(v) => {
            setNickname(v);
            if (errorText) setErrorText('');
          }}
          placeholder="닉네임(영문,한글,숫자 조합 10글자 이내)"
          maxLength={10}
          error={!!errorText}
          errorText={errorText}
        />
      </div>
    </AuthOnboardingLayout>
  );
}
